import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Play, CheckCircle, Clock, AlertCircle, FileText, Layout, ArrowRight, Lock } from 'lucide-react'; // 🔴 ضفنا أيقونة Lock
import Footer from './Footer';
import './ProfileCard.css';

const ProfileCard = ({ darkMode }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [quizUrl, setQuizUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [lessonProgress, setLessonProgress] = useState({});
  
  // 🔴 State جديدة لحفظ حالة فتح أو قفل كل درس
  const [lessonAccess, setLessonAccess] = useState({}); 
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // جلب التقدم لكل درس
  const fetchLessonProgress = async (lessonId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/video-progress/${lessonId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setLessonProgress(prev => ({ ...prev, [lessonId]: response.data }));
    } catch (err) {
      setLessonProgress(prev => ({ ...prev, [lessonId]: { percentage: 0, status: 'not_started' } }));
    }
  };

  // 🔴 دالة جديدة للتحقق من صلاحية الدخول للدرس
  const fetchLessonAccess = async (lessonId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/lessons/${lessonId}/can-access`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      // هنحفظ حالة الدرس (true أو false)
      setLessonAccess(prev => ({ ...prev, [lessonId]: response.data.status }));
    } catch (err) {
      // لو حصل إيرور هنقفل الدرس احتياطياً
      setLessonAccess(prev => ({ ...prev, [lessonId]: false }));
    }
  };

  const fetchLessons = useCallback(async () => {
    setLoading(true);
    try {
      const packageId = localStorage.getItem('package_id');
      const authToken = localStorage.getItem('authToken');
      if (!authToken || !packageId) { navigate('/Signin'); return; }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/lessons/package/${packageId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      setLessons(response.data);
      
      // جلب التقدم وصلاحية الدخول لكل درس في نفس الوقت
      response.data.forEach(lesson => {
        fetchLessonProgress(lesson.id);
        fetchLessonAccess(lesson.id); // 🔴 استدعاء الدالة الجديدة هنا
      });

    } catch (err) {
      console.error("Error fetching lessons", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchLessons();
    const fetchQuiz = async () => {
      const packageId = localStorage.getItem('package_id');
      const token = localStorage.getItem('authToken');
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/mcqs/package/${packageId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.mcqs?.length > 0) setQuizUrl(res.data.mcqs[0].url);
      } catch (e) { }
    };
    fetchQuiz();
  }, [fetchLessons]);

  const handleEnterLesson = (lessonId) => {
    localStorage.setItem('lesson_id', lessonId);
    navigate('/Video');
  };

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="dashboard-content">

        {/* Header Section */}
        <header className="dashboard-header">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1>مرحباً بك في <span>محاضراتك</span></h1>
            <p>تابع تقدمك واستكمل مسيرتك التعليمية اليوم</p>
          </motion.div>

          {quizUrl && (
            <motion.a
              href={quizUrl} target="_blank" rel="noreferrer"
              className="quiz-banner-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FileText size={20} />
              <span>خوض الاختبار الشهري</span>
            </motion.a>
          )}
        </header>

        <div className="stats-row">
          <div className="stat-card">
            <Layout className="icon" />
            <div>
              <h4>{lessons.length}</h4>
              <p>إجمالي المحاضرات</p>
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="lessons-wrapper">
          {loading ? (
            <div className="skeleton-loader">جاري تحميل محاضراتك...</div>
          ) : lessons.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} />
              <p>لا توجد محاضرات متاحة في هذه الباقة حالياً</p>
            </div>
          ) : (
            <div className="lessons-grid">
              <AnimatePresence>
                {lessons.map((lesson, index) => {
                  const progress = lessonProgress[lesson.id] || { percentage: 0, status: 'not_started' };
                  
                  // 🔴 التحقق من حالة الدرس (هل هو متاح أم لا؟) - الافتراضي false لحد ما الداتا تيجي
                  const canAccess = lessonAccess[lesson.id] === true; 

                  return (
                    <motion.div
                      key={lesson.id}
                      className="lesson-modern-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="card-top">
                        <div className="image-container">
                          <img
                            src={`https://leader-acadmy.hwnix.com/storage/${lesson.image}`}
                            alt="Lesson Thumbnail" 
                            style={{ filter: canAccess ? 'none' : 'grayscale(80%)' }} // 🔴 تأثير أبيض وأسود لو الدرس مقفول
                          />
                          <div className={`status-badge ${progress.status}`}>
                            {progress.status === 'completed' && <CheckCircle size={14} />}
                            {progress.status === 'in_progress' && <Clock size={14} />}
                            {progress.status === 'completed' ? 'تمت المشاهدة' : progress.status === 'in_progress' ? 'قيد التقدم' : 'جديد'}
                          </div>
                        </div>

                        <div className="lesson-info">
                          <h3>{lesson.title}</h3>
                          <p>{lesson.description || "استمتع بمشاهدة شرح تفصيلي ومبسط لهذا الدرس."}</p>
                        </div>
                      </div>

                      <div className="card-bottom">
                        <div className="progress-section">
                          <div className="progress-text">
                            <span>نسبة الإنجاز</span>
                            <span>{progress.percentage}%</span>
                          </div>
                          <div className="progress-bar-container">
                            <motion.div
                              className="progress-bar-fill"
                              initial={{ width: 0 }}
                              animate={{ width: `${progress.percentage}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                            />
                          </div>
                        </div>

                        {/* 🔴 شرط عرض الزرار بناءً على الـ API */}
                        {canAccess ? (
                          <button
                            className="enter-lesson-btn"
                            onClick={() => handleEnterLesson(lesson.id)}
                          >
                            <span>ابدأ الآن</span>
                            <Play size={18} fill="currentColor" />
                          </button>
                        ) : (
                          // زرار مقفول وشكله باهت ومبيعملش حاجة (أفضل من إخفاءه تماماً)
                          <button
                            className="enter-lesson-btn locked"
                            disabled
                            style={{ backgroundColor: '#ccc', color: '#666', cursor: 'not-allowed' }}
                            title="يجب اجتياز الدرس أو الاختبار السابق أولاً"
                          >
                            <span>مغلق</span>
                            <Lock size={18} />
                          </button>
                        )}

                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfileCard;